import { useQuery } from 'convex/react';
import { FileQuestion } from 'lucide-react';

import { api } from '../../../convex/_generated/api';
import { useAuth } from '@/hooks/useAuth';
import { ProjectCard } from './ProjectCard';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';

interface ProjectListProps {
  onCreateClick: () => void;
}

/**
 * Reactive list of the caller's projects.
 *
 * Three render states:
 *   - loading: centred spinner
 *   - empty: editorial empty-state with a CTA
 *   - populated: responsive grid of ProjectCards
 */
export function ProjectList({ onCreateClick }: ProjectListProps) {
  const { token } = useAuth();
  const projects = useQuery(api.projects.listMine, token ? { token } : 'skip');

  if (!token || projects === undefined) {
    return (
      <div className="flex justify-center py-20">
        <LoadingSpinner label="Loading your projects…" />
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="grid grid-cols-12 gap-6 py-16">
        <div className="col-span-12 md:col-span-2">
          <FileQuestion className="h-6 w-6 text-ochre" aria-hidden />
        </div>
        <div className="col-span-12 md:col-span-10">
          <h2 className="font-serif text-display-sm text-ink">
            No projects yet.
          </h2>
          <p className="mt-3 max-w-prose text-base leading-relaxed text-graphite">
            A project bundles a questionnaire, a set of lo-fi sketches, the
            agent pipeline, and the resulting artefacts. Start a new one
            and AURA will guide you through fourteen short questions
            before you upload anything.
          </p>
          <div className="mt-8">
            <Button onClick={onCreateClick}>Start a new project</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
      {projects.map((p) => (
        <ProjectCard key={p._id} project={p} />
      ))}
    </div>
  );
}
